import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios defaults — send cookies + bearer
axios.defaults.withCredentials = true;

const AuthCtx = createContext(null);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = signed-in
  const [token, setToken] = useState(() => localStorage.getItem("e65_token") || null);

  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    else delete axios.defaults.headers.common["Authorization"];
  }, [token]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  // Presence heartbeat — keeps the live "online" counter accurate.
  useEffect(() => {
    if (!user || user === false) return;
    const ping = () => axios.post(`${API}/presence/ping`).catch(() => {});
    ping();
    const id = setInterval(ping, 4 * 60 * 1000); // every 4 minutes
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.access_token) {
      localStorage.setItem("e65_token", data.access_token);
      setToken(data.access_token);
    }
    setUser(data.user);
    return data.user;
  };

  const register = async (email, username, password) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, username, password });
    if (data.access_token) {
      localStorage.setItem("e65_token", data.access_token);
      setToken(data.access_token);
    }
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    localStorage.removeItem("e65_token");
    setToken(null);
    setUser(false);
  };

  const updateProfile = async (patch) => {
    const { data } = await axios.patch(`${API}/users/me`, patch);
    setUser(data);
    return data;
  };

  return (
    <AuthCtx.Provider value={{ user, token, login, register, logout, updateProfile, refresh: fetchMe }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
